import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { getMaintenanceStatus } from '../api/system';
import { useTranslation } from '../i18n';
import { analytics } from '../hooks/useAnalytics';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';

export default function LoginPage() {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password,     setPassword]    = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe,   setRememberMe]  = useState(true);
  const [error,        setError]       = useState<string | null>(null);
  const [loading,      setLoading]     = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);

  const setAuth  = useAuthStore((s) => s.setAuth);
  const syncNotificationStatus = useAuthStore((s) => s.syncNotificationStatus);
  const navigate = useNavigate();
  const { t, direction } = useTranslation();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    analytics.trackLoginStarted();

    try {
      // Check maintenance status FIRST before attempting login
      try {
        const maintenanceStatus = await getMaintenanceStatus('web');
        if (maintenanceStatus.isInMaintenance) {
          useAuthStore.getState().setMaintenanceMode(true);
          navigate('/maintenance', { replace: true });
          return;
        }
      } catch (err) {
        // If maintenance check fails, show error
        console.error('Failed to check maintenance status:', err);
        setError(t('verify_system_error'));
        return;
      }

      // Only proceed with login if maintenance is not enabled
      const identifier = emailOrPhone.includes('@') ? emailOrPhone.toLowerCase().trim() : emailOrPhone.trim();
      const res = await apiClient.post('/auth/login', {
        email_or_phone: identifier,
        password,
        remember_me: rememberMe,
      });
      const { access_token, refresh_token, user, apiVersion } = res.data.data as {
        access_token:  string;
        refresh_token: string;
        user: Parameters<typeof setAuth>[2];
        apiVersion?: string;
      };
      setAuth(access_token, refresh_token, user);
      analytics.trackUserLoggedIn();
      // P3.3 + P3.5: set GA4 user properties so reports can be sliced
      analytics.setUserId(user.id);
      analytics.setUserProperties({
        buildingId: user.building.id,
        userRole: user.super_admin ? 'super_admin' : user.is_admin ? 'admin' : 'resident',
      });

      // Notify version check on login
      if (typeof window !== 'undefined' && (window as any).__versionCheck) {
        (window as any).__versionCheck.handleAuthResponse(apiVersion);
      }

      await syncNotificationStatus();
      navigate('/', { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const code = err.response?.data?.error?.code;
        if (code === 'USER_INACTIVE') {
          setShowInactiveModal(true);
          analytics.trackUserLoginFailed('USER_INACTIVE');
        } else if (err.response?.status === 429 || code === 'RATE_LIMIT_EXCEEDED') {
          setError(t('too_many_login_attempts'));
          analytics.trackUserLoginFailed('RATE_LIMIT_EXCEEDED');
        } else if (err.response?.status === 401) {
          setError(t('invalid_credentials'));
          analytics.trackUserLoginFailed('INVALID_CREDENTIALS');
        } else {
          setError(t('something_wrong'));
          analytics.trackUserLoginFailed('UNKNOWN_ERROR');
        }
      } else {
        setError(t('something_wrong'));
        analytics.trackUserLoginFailed('NETWORK_ERROR');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div dir={direction} style={{
      // 100dvh (not a fixed 884px floor): the floor forced the page taller
      // than the viewport, so when the Android soft keyboard opened on focus,
      // the browser scrolled the whole screen up to reveal the field. dvh
      // tracks the dynamic viewport (keyboard-aware) — matches every other page.
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--surface)',
      padding: '1.5rem',
    }}>
      {/* Background blur blobs */}
      <div style={{
        position: 'fixed', zIndex: -1,
        top: '-10%', right: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(0,91,192,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'fixed', zIndex: -1,
        bottom: '-10%', left: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(95,92,120,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />

      <main style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: 'var(--manrope)',
            fontSize: '2.25rem',
            fontWeight: 800,
            letterSpacing: '-0.04em',
            color: 'var(--primary)',
            margin: 0,
          }}>Lot'N'Go</h1>
          <p className="body-md" style={{ marginTop: '0.5rem', letterSpacing: '0.03em' }}>
            {t('smart_mobility_solution')}
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--surface-lowest)',
          borderRadius: '12px',
          padding: '2.5rem',
          boxShadow: 'var(--whisper)',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}>
          <form onSubmit={handleSubmit} id="login-form" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Email or Phone */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label className="form-label" htmlFor="email-or-phone">{t('email_or_phone')}</label>
              <div style={{ position: 'relative' }}>
                <span className="material-symbols-outlined" style={{
                  position: 'absolute',
                  [direction === 'rtl' ? 'right' : 'left']: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--on-surface-3)', fontSize: '20px',
                  pointerEvents: 'none',
                }}>mail</span>
                <input
                  id="email-or-phone"
                  type="text"
                  value={emailOrPhone}
                  onChange={(e) => setEmailOrPhone(e.target.value)}
                  placeholder="e.g., name@email.com or +123456789"
                  required
                  autoComplete="email"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    paddingLeft: direction === 'rtl' ? '1rem' : '3rem',
                    paddingRight: direction === 'rtl' ? '3rem' : '1rem',
                    paddingTop: '1rem', paddingBottom: '1rem',
                    borderRadius: '8px', border: 'none',
                    background: 'var(--surface-low)',
                    fontFamily: 'var(--inter)', fontSize: '14px',
                    color: 'var(--on-surface)',
                    outline: '2px solid transparent',
                    transition: 'outline 0.15s',
                    textAlign: direction === 'rtl' ? 'right' : 'left',
                  }}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label className="form-label" htmlFor="password" style={{ margin: 0 }}>{t('password')}</label>
              <div style={{ position: 'relative' }}>
                <span className="material-symbols-outlined" style={{
                  position: 'absolute',
                  [direction === 'rtl' ? 'right' : 'left']: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--on-surface-3)', fontSize: '20px',
                  pointerEvents: 'none',
                }}>lock</span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    paddingLeft: direction === 'rtl' ? '3rem' : '3rem',
                    paddingRight: direction === 'rtl' ? '3rem' : '3rem',
                    paddingTop: '1rem', paddingBottom: '1rem',
                    borderRadius: '8px', border: 'none',
                    background: 'var(--surface-low)',
                    fontFamily: 'var(--inter)', fontSize: '14px',
                    color: 'var(--on-surface)',
                    outline: '2px solid transparent',
                    transition: 'outline 0.15s',
                    textAlign: direction === 'rtl' ? 'right' : 'left',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('hide_password') : t('show_password')}
                  aria-pressed={showPassword}
                  style={{
                    position: 'absolute',
                    [direction === 'rtl' ? 'left' : 'right']: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--on-surface-3)',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }} aria-hidden>
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              <button
                type="button"
                onClick={() => navigate('/reset-password')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--primary)',
                  textDecoration: 'none',
                  textAlign: direction === 'rtl' ? 'right' : 'left',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                {t('forgot_password')}
              </button>
            </div>

            {error && (
              <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 0 }}>{error}</p>
            )}

            {/* Keep me logged in */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: 'var(--primary)',
                }}
              />
              <label htmlFor="remember-me" style={{
                fontSize: '0.875rem',
                color: 'var(--on-surface)',
                cursor: 'pointer',
                userSelect: 'none',
                margin: 0,
              }}>
                {t('keep_logged_in')}
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
                color: 'var(--on-primary)',
                fontFamily: 'var(--manrope)',
                fontSize: '15px',
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 14px rgba(0,91,192,0.25)',
              }}
            >
              {loading ? t('signing_in') : t('sign_in')}
              {!loading && (
                <DirectionalIcon icon="arrow_forward" style={{ fontSize: '20px' }} />
              )}
            </button>
          </form>

          {/* OR divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ height: '1px', flex: 1, background: 'var(--surface-high)' }} />
            <span style={{
              fontSize: '10px', fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'var(--on-surface-3)',
            }}>{t('or')}</span>
            <div style={{ height: '1px', flex: 1, background: 'var(--surface-high)' }} />
          </div>

          {/* Secondary CTA */}
          <button
            onClick={async () => {
              try {
                const status = await getMaintenanceStatus('web');
                if (status.isInMaintenance) {
                  useAuthStore.getState().setMaintenanceMode(true);
                  navigate('/maintenance', { replace: true });
                } else {
                  navigate('/register', { replace: true });
                }
              } catch (err) {
                // If check fails, proceed to register
                console.error('Failed to check maintenance status:', err);
                navigate('/register', { replace: true });
              }
            }}
            style={{
              display: 'block', width: '100%', boxSizing: 'border-box',
              padding: '1rem',
              borderRadius: '8px',
              background: 'var(--surface-high)',
              color: 'var(--primary)',
              fontFamily: 'var(--manrope)',
              fontSize: '14px', fontWeight: 700,
              textAlign: 'center',
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-highest)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface-high)')}
          >
            {t('new_user_join')}
          </button>

          {/* No invite code — in-app lead capture */}
          <button
            type="button"
            onClick={() => navigate('/join')}
            style={{
              display: 'block',
              width: '100%',
              background: 'none',
              border: 'none',
              textAlign: 'center',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--primary)',
              cursor: 'pointer',
              marginTop: '-0.5rem',
              fontFamily: 'var(--inter)',
            }}
          >
            {t('no_invite_code')}
          </button>
        </div>

        {/* Video tour link */}
        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
          <a
            href="https://youtube.com/shorts/apAekxLcbRA"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--on-surface-3)',
              textDecoration: 'none',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--on-surface-3)')}
          >
            {t('watch_tour')}
          </a>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '2rem', textAlign: direction === 'rtl' ? 'right' : 'center', padding: '0 1rem' }}>
          <p className="body-md" style={{ lineHeight: 1.6, fontSize: '12px' }}>
            {t('signin_terms')}{' '}
            <a
              href="https://lotngo.com/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              {t('terms_of_service')}
            </a>
            {' '}{t('and')}{' '}
            <a
              href="https://lotngo.com/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              {t('privacy_policy')}
            </a>
            {' '}{t('of_lotngo')}.
          </p>
        </div>
      </main>

      {/* Bottom gradient strip */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0,
        width: '100%', height: '4px',
        background: 'linear-gradient(to right, #005bc0, #d8e2ff, #005bc0)',
        pointerEvents: 'none',
      }} />

      {/* Account deactivated modal */}
      {showInactiveModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1.5rem',
          background: 'rgba(26,28,30,0.55)',
          backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: 'var(--surface-lowest)',
            borderRadius: '16px',
            padding: '2rem',
            width: '100%',
            maxWidth: '360px',
            boxShadow: '0 24px 64px -8px rgba(43,52,55,0.22)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
            textAlign: 'center',
          }}>
            <div style={{
              width: '56px', height: '56px',
              borderRadius: '50%',
              background: 'var(--error-container)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto',
            }}>
              <span className="material-symbols-outlined" style={{ color: 'var(--error)', fontSize: '28px' }}>
                block
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <h2 style={{
                fontFamily: 'var(--manrope)',
                fontSize: '1.125rem',
                fontWeight: 700,
                color: 'var(--on-surface)',
                margin: 0,
              }}>
                {t('account_deactivated')}
              </h2>
              <p style={{
                fontFamily: 'var(--inter)',
                fontSize: '0.875rem',
                color: 'var(--on-surface-2)',
                margin: 0,
                lineHeight: 1.6,
              }}>
                {t('account_suspended_message')}
              </p>
            </div>

            <button
              onClick={() => {
                setShowInactiveModal(false);
                setEmailOrPhone('');
                setPassword('');
              }}
              style={{
                width: '100%',
                padding: '0.875rem',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--error-container)',
                color: 'var(--error)',
                fontFamily: 'var(--manrope)',
                fontSize: '15px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {t('back_to_login')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
